import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * « Tirer pour actualiser » de toute l'app (PWA iPhone + Android : en mode
 * standalone il n'y a ni bouton ni geste natif pour recharger).
 *
 * Le geste ne démarre que sur écran tactile (événements touch), quand la zone
 * touchée est tout en haut — la page ET tout conteneur défilant qui la contient
 * — et hors des fenêtres (dialog / aria-modal / data-no-pull-refresh).
 *
 * Performance : écouteurs PASSIFS uniquement (le navigateur n'attend jamais le
 * JS pour défiler) ; la roue est déplacée en direct dans le DOM, sans rendu
 * React pendant le geste.
 *
 * Par défaut on recharge la page. Une page peut fournir son propre
 * rafraîchissement avec `usePageRefresh(fn)` (la feuille de match ne recharge
 * que son entrée, sans relancer l'app).
 */

const PULL_THRESHOLD = 64; // distance (après résistance) qui déclenche
const MAX_PULL = 96;
const RESISTANCE = 0.5;
const START_SLOP = 8;
const MIN_SPIN_MS = 400; // la roue reste visible au moins ce temps
const REST_OFFSET = 52; // position de la roue pendant l'actualisation

const PullToRefreshContext = createContext({
  registerRefresh: () => () => { },
  getRefresh: () => null
});

export const PullToRefreshProvider = ({ children }) => {
  // Pile : la page la plus récemment montée gagne, et retrouve la précédente
  // en se démontant (ex. feuille de match intégrée dans la page Compétitions).
  const handlers = useRef([]);
  const registerRefresh = useCallback((fn) => {
    handlers.current.push(fn);
    return () => {
      handlers.current = handlers.current.filter((h) => h !== fn);
    };
  }, []);
  const getRefresh = useCallback(() => handlers.current[handlers.current.length - 1] || null, []);
  const value = useMemo(() => ({ registerRefresh, getRefresh }), [registerRefresh, getRefresh]);
  return <PullToRefreshContext.Provider value={value}>{children}</PullToRefreshContext.Provider>;
};

/** Remplace le rechargement de la page par `handler` (peut renvoyer une promesse). */
// eslint-disable-next-line react-refresh/only-export-components -- local context hook co-located with its provider component (established convention; dev-only HMR hint)
export const usePageRefresh = (handler) => {
  const { registerRefresh } = useContext(PullToRefreshContext);
  const latest = useRef(handler);
  useEffect(() => { latest.current = handler; });
  useEffect(() => registerRefresh(() => latest.current?.()), [registerRefresh]);
};

/** Tout est en haut, de la zone touchée jusqu'au conteneur de la page inclus. */
const isAtTop = (target, container) => {
  for (let el = target; el; el = el.parentElement) {
    if (el.scrollTop > 0) return false;
    if (el === container) return true;
  }
  return true;
};

const isExcluded = (target) => Boolean(
  target?.closest?.('[role="dialog"], [aria-modal="true"], [data-no-pull-refresh]')
);

const reloadPage = () => window.location.reload();

/**
 * Roue + geste. `containerRef` : le conteneur défilant de la page ;
 * `resetKey` : change quand ce conteneur est remonté (changement de route).
 */
export const PullToRefreshIndicator = ({ containerRef, enabled = true, resetKey, onReload = reloadPage }) => {
  const { getRefresh } = useContext(PullToRefreshContext);
  const indicatorRef = useRef(null);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  const latest = useRef({ enabled, onReload });
  useEffect(() => { latest.current = { enabled, onReload }; });

  const place = useCallback((distance, animate) => {
    const el = indicatorRef.current;
    if (!el) return;
    el.style.transition = animate ? 'transform 200ms ease, opacity 200ms ease' : 'none';
    el.style.transform = `translate(-50%, ${distance}px)`;
    el.style.opacity = distance > 0 ? String(Math.min(1, distance / PULL_THRESHOLD)) : '0';
    const icon = el.firstElementChild;
    if (icon && !refreshingRef.current) icon.style.transform = `rotate(${distance * 4}deg)`;
    el.dataset.armed = distance >= PULL_THRESHOLD ? 'true' : 'false';
  }, []);

  const runRefresh = useCallback(async () => {
    refreshingRef.current = true;
    setRefreshing(true);
    place(REST_OFFSET, true);
    const handler = getRefresh();
    if (!handler) {
      latest.current.onReload();
      return; // la page se recharge : la roue tourne jusqu'au bout
    }
    const started = Date.now();
    try {
      await handler();
    } finally {
      const wait = Math.max(0, MIN_SPIN_MS - (Date.now() - started));
      setTimeout(() => {
        refreshingRef.current = false;
        setRefreshing(false);
        place(0, true);
      }, wait);
    }
  }, [getRefresh, place]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const g = { tracking: false, pulling: false, startX: 0, startY: 0, distance: 0 };

    const onStart = (e) => {
      g.tracking = false;
      g.pulling = false;
      g.distance = 0;
      if (!latest.current.enabled || refreshingRef.current || e.touches.length !== 1) return;
      if (isExcluded(e.target) || !isAtTop(e.target, container)) return;
      g.tracking = true;
      g.startX = e.touches[0].clientX;
      g.startY = e.touches[0].clientY;
    };

    const onMove = (e) => {
      if (!g.tracking) return;
      const dx = e.touches[0].clientX - g.startX;
      const dy = e.touches[0].clientY - g.startY;
      if (!g.pulling) {
        // Geste horizontal (carrousel) ou vers le haut : on laisse faire.
        if (Math.abs(dx) > START_SLOP && Math.abs(dx) >= dy) { g.tracking = false; return; }
        if (dy < -START_SLOP) { g.tracking = false; return; }
        if (dy <= START_SLOP || !isAtTop(e.target, container)) return;
        g.pulling = true;
      }
      g.distance = Math.min(MAX_PULL, Math.max(0, (dy - START_SLOP) * RESISTANCE));
      place(g.distance, false);
    };

    const onEnd = () => {
      const armed = g.pulling && g.distance >= PULL_THRESHOLD;
      const wasPulling = g.pulling;
      g.tracking = false;
      g.pulling = false;
      g.distance = 0;
      if (armed) runRefresh();
      else if (wasPulling) place(0, true);
    };

    const onCancel = () => {
      if (g.pulling) place(0, true);
      g.tracking = false;
      g.pulling = false;
      g.distance = 0;
    };

    const opts = { passive: true };
    container.addEventListener('touchstart', onStart, opts);
    container.addEventListener('touchmove', onMove, opts);
    container.addEventListener('touchend', onEnd, opts);
    container.addEventListener('touchcancel', onCancel, opts);
    return () => {
      container.removeEventListener('touchstart', onStart, opts);
      container.removeEventListener('touchmove', onMove, opts);
      container.removeEventListener('touchend', onEnd, opts);
      container.removeEventListener('touchcancel', onCancel, opts);
    };
  }, [containerRef, resetKey, place, runRefresh]);

  return (
    <div
      ref={indicatorRef}
      data-testid="pull-to-refresh"
      data-refreshing={refreshing ? 'true' : 'false'}
      role="status"
      aria-label={refreshing ? 'Actualisation…' : undefined}
      className="pointer-events-none fixed left-1/2 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-[#1a1a1a] shadow-lg"
      style={{
        top: 'calc(env(safe-area-inset-top, 0px) - 40px)',
        transform: 'translate(-50%, 0px)',
        opacity: 0,
        zIndex: 10000
      }}
    >
      <Loader2 className={`h-5 w-5 text-[#d4845a] ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
    </div>
  );
};
